import { useCallback, useEffect, useRef } from "react";
import { shallowEqual } from "react-redux";
import io, { Socket } from "socket.io-client";
import { notifyToast } from "../components/notifyMessage/NotifyHelper";
import { checkIsMuted, getInfoByType } from "../functions";
import { getAllChats, getUserMetaData } from "../store/chatActions";
import { chatsSlice } from "../store/chatSlice";
import {
  ChatListType,
  ChatMessage,
  MembersGroupInfo,
  ResponseSocketDeleteMessage,
  UserMetaData,
  UserOnline,
} from "../types";
import { ChatEmitsEnum, ConversationTypeEnum } from "../types/enums";
import { useChatDispatch, useChatSelector } from "./reduxHook";
import { OpenAlert } from "../../../redux/alert/action";
import scrollIntoMessage from "../../../utils/scroll-to-message";

const { actions } = chatsSlice;

const useChatSocket = () => {
  const dispatch = useChatDispatch();

  const { userInfo, token, isOpen, usersMetaData, selectedChat } =
    useChatSelector(
      (state: any) => ({
        token: state.userToken,
        userInfo: state.userInfo,
        isOpen: state.chats.isOpen,
        usersMetaData: state.chats.usersMetaData,
        selectedChat: state.chats.selectedChat,
      }),
      shallowEqual
    );

  const socketRef = useRef<Socket | undefined>(undefined);
  const userInfoRef = useRef(userInfo);
  const isOpenRef = useRef(isOpen);
  const selectedChatRef = useRef(selectedChat);
  const usersMetaDataRef = useRef<UserMetaData>(usersMetaData);

  const showNotifyToast = useCallback(
    (data: ChatMessage, photofile: string) => {
      const shouldShowNotification =
        !isOpenRef.current || selectedChatRef.current.chatId !== data.chatId;
      if (
        !checkIsMuted() &&
        shouldShowNotification &&
        data.senderId !== userInfoRef.current?.id
      ) {
        notifyToast({
          userInfoId: userInfoRef.current.id,
          message: data,
          photofile,
        });
      }
    },
    []
  );

  const handleNewMessage = useCallback(
    (data: ChatMessage) => {
      let photoFile = "";
      let newMessage = { ...data };
      let user = {};
      if (Object.keys(data).includes("receiverId")) {
        newMessage = {
          ...newMessage,
          type: ConversationTypeEnum.PERSONAL,
        };

        if (usersMetaDataRef.current?.[newMessage?.senderId!]) {
          photoFile =
            usersMetaDataRef.current?.[newMessage?.senderId!]?.photoFile!;
          user = usersMetaDataRef.current?.[newMessage?.senderId!];
          dispatch(
            actions.setUserFromMetaData({
              ...newMessage,
              user: user,
            })
          );
        } else {
          photoFile = newMessage?.senderData?.photoFile ?? "";
          dispatch(actions.setMetaData(newMessage?.senderData!));
          dispatch(getUserMetaData(newMessage?.senderId!)).then((response) => {
            photoFile = response?.data?.photoFile ?? "";
            user = {
              id: response?.data?.id,
              username: response?.data?.username,
              photoFile: response?.data?.photoFile,
            };
            dispatch(
              actions.setUserFromMetaData({
                chatId: newMessage?.chatId,
                user: user,
              })
            );
          });
        }
      } else {
        photoFile = selectedChatRef.current.photoFile!;
        newMessage = {
          ...newMessage,
          chatId: newMessage?.conversationId,
          type: ConversationTypeEnum.GROUP,
        };
      }
      showNotifyToast(newMessage, photoFile);
      dispatch(actions.setNewMessage(newMessage));
      dispatch(actions.setAdjustedMessages());
      if (selectedChatRef.current.chatId === newMessage.chatId) {
        setTimeout(() => {
          scrollIntoMessage(newMessage?._id);
        }, 250);
      }
    },
    [dispatch, showNotifyToast]
  );

  const initializeSocket = useCallback((): Socket | undefined => {
    // if(!IsBeta) return  undefined
    const socket = io("https://ws.chainoflegends.com", {
      auth: { token: token },
    });

    socket.off(ChatEmitsEnum.NEW_MESSAGE);
    socket.on(ChatEmitsEnum.NEW_MESSAGE, handleNewMessage);

    socket.on(ChatEmitsEnum.ONLINE_USERS, (data: UserOnline) => {
      dispatch(actions.updatedOnlineConversationList(data));
    });

    socket.on(ChatEmitsEnum.GROUP_ONLINE_USERS, (data: MembersGroupInfo) => {
      dispatch(actions.updateMemberGroup(data));
    });

    socket.on(ChatEmitsEnum.RATE_LIMIT, (data: Record<string, any>) => {
      dispatch(
        OpenAlert({
          msg:
            data?.error ??
            "You are sending messages too fast. Please slow down.",
          open: true,
          variant: "error",
        })
      );
    });

    socket.on(ChatEmitsEnum.USER_ACCESS, (data: { isAdmin: boolean }) => {
      dispatch(actions.setIsAdmin(data));
    });

    socket.on(
      ChatEmitsEnum.DELETE_MESSAGE,
      (data: ResponseSocketDeleteMessage) => {
        const { conversationId, _id, message } = data;
        dispatch(
          actions.replaceMessageFromConversation({
            conversationId,
            messageId: _id,
            message,
          })
        );
        dispatch(actions.setAdjustedMessages());
        dispatch(getAllChats());
      }
    );

    socket.off(ChatEmitsEnum.CONVERSATION_CHANGED);
    socket.on(ChatEmitsEnum.CONVERSATION_CHANGED, (message: ChatListType) => {
      dispatch(
        actions.addNewConversationToConversationList({
          message: getInfoByType(message),
          userInfoId: userInfo?.id,
        })
      );
    });

    return socket;
  }, [dispatch, token]);

  useEffect(() => {
    if (token && userInfo?.id) {
      socketRef.current = initializeSocket();
    } else if (!token || !userInfo?.id) {
      socketRef.current?.disconnect();
      socketRef.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userInfo?.id]);

  useEffect(() => {
    if (!usersMetaData?.[userInfo?.id]) {
      dispatch(getUserMetaData(userInfo?.id));
    } else {
      usersMetaDataRef.current = usersMetaData;
    }
  }, [usersMetaData, userInfo?.id, dispatch]);

  useEffect(() => {
    userInfoRef.current = userInfo;
    isOpenRef.current = isOpen;
    selectedChatRef.current = selectedChat;
    usersMetaDataRef.current = usersMetaData;
  }, [userInfo, isOpen, selectedChat, usersMetaData]);

  useEffect(
    () => () => {
      socketRef.current?.disconnect();
    },
    []
  );

  return socketRef.current;
};

export default useChatSocket;
